import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { FonteInput } from "@vetbolso/contracts";
import { dataBR } from "@vetbolso/core";
import { useState } from "react";
import { api, ler, queryClient } from "../api.ts";
import { useEu } from "../router.tsx";
import { Aviso, Botao, Cabecalho, Campo, Carregando, Cartao, Dialogo, Entrada, ErroCarga, Selo, Tabela, Vazio } from "../ui.tsx";

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const UM_ANO_MS = 365 * 86_400_000;
const antiga = (acesso: string) => Date.now() - new Date(acesso).getTime() > UM_ANO_MS;

type Fonte = Partial<Record<keyof FonteInput, unknown>>;
const CAMPOS: { k: keyof FonteInput; rotulo: string; tipo?: string; dica?: string; largo?: boolean }[] = [
  { k: "sigla", rotulo: "Sigla *", dica: "Como aparece no chip do app (ex.: Plumb's 10ª ed.)" },
  { k: "titulo", rotulo: "Título *", largo: true },
  { k: "orgao", rotulo: "Autor / órgão" },
  { k: "ano", rotulo: "Ano" },
  { k: "versao", rotulo: "Versão / edição" },
  { k: "tipo", rotulo: "Tipo", dica: "bula, norma, livro, artigo, diretriz…" },
  { k: "url", rotulo: "URL", tipo: "url", largo: true },
  { k: "doi", rotulo: "DOI" },
  { k: "acesso", rotulo: "Conferida em *", tipo: "date" },
  { k: "status", rotulo: "Status" },
  { k: "nota", rotulo: "Nota", largo: true },
];

/** Formulário da fonte; valida com o mesmo zod da API antes de enviar. */
function FormFonte({
  inicial,
  novo,
  aoSalvar,
  pendente,
  erro,
}: {
  inicial: Fonte;
  novo?: boolean;
  aoSalvar: (f: FonteInput) => void;
  pendente: boolean;
  erro?: unknown;
}) {
  const [erros, setErros] = useState<Record<string, string>>({});
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const bruto: Record<string, unknown> = { id: novo ? fd.get("id") : inicial.id, semFato: fd.get("semFato") === "on" };
        for (const c of CAMPOS) {
          const v = String(fd.get(c.k) ?? "").trim();
          bruto[c.k] = v === "" ? (c.k === "status" ? undefined : null) : v;
        }
        const r = FonteInput.safeParse(bruto);
        if (!r.success) return setErros(Object.fromEntries(r.error.issues.map((i) => [String(i.path[0]), i.message])));
        setErros({});
        aoSalvar(r.data);
      }}
    >
      {erro ? <Aviso>{msg(erro)}</Aviso> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {novo ? (
          <Campo rotulo="Id *" dica="Prefixo do arquivo de pesquisa + código, ex.: E.B19" erro={erros.id}>
            <Entrada name="id" className="font-mono" required />
          </Campo>
        ) : null}
        {CAMPOS.map((c) => (
          <Campo key={c.k} rotulo={c.rotulo} dica={c.dica} erro={erros[c.k]} className={c.largo ? "sm:col-span-2" : undefined}>
            <Entrada name={c.k} type={c.tipo ?? "text"} defaultValue={inicial[c.k] == null ? "" : String(inicial[c.k])} />
          </Campo>
        ))}
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" name="semFato" defaultChecked={!!inicial.semFato} />
          Citada inteira (norma, por exemplo): dispensa fato nas refs
        </label>
      </div>
      <Botao type="submit" variante="primario" className="self-start" disabled={pendente}>
        {pendente ? "Salvando…" : "Salvar fonte"}
      </Botao>
    </form>
  );
}

export function PaginaFontes() {
  const [q, setQ] = useState("");
  const [nova, setNova] = useState(false);
  const eu = useEu();
  const navegar = useNavigate();
  const { data, error } = useQuery({ queryKey: ["fontes", q], queryFn: () => ler(api.fontes.$get({ query: { q: q || undefined } })) });
  const criar = useMutation({
    mutationFn: (f: FonteInput) => ler(api.fontes.$post({ json: f })),
    onSuccess: (f) => {
      queryClient.invalidateQueries({ queryKey: ["fontes"] });
      navegar({ to: "/fontes/$id", params: { id: f.id } });
    },
  });
  const editor = eu?.papel === "admin" || eu?.papel === "editor";

  return (
    <>
      <Cabecalho
        titulo="Fontes e fatos"
        sub="Fonte é o documento; fato é o trecho conferido nele. Toda ref de ficha aponta para os dois."
        acoes={
          editor ? (
            <Botao variante="primario" onClick={() => setNova(true)}>
              Nova fonte
            </Botao>
          ) : null
        }
      />
      <Entrada
        className="max-w-sm"
        type="search"
        placeholder="Buscar por id, sigla ou título"
        onKeyDown={(e) => e.key === "Enter" && setQ(e.currentTarget.value.trim())}
        aria-label="Buscar"
      />
      {error ? (
        <ErroCarga erro={error} />
      ) : !data ? (
        <Carregando />
      ) : !data.length ? (
        <Vazio>Nenhuma fonte.</Vazio>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <th>Id</th>
              <th>Fonte</th>
              <th>Tipo</th>
              <th>Conferida</th>
              <th className="text-right">Fatos</th>
              <th className="text-right">Usos</th>
            </tr>
          </thead>
          <tbody>
            {data.map((f) => (
              <tr key={f.id} className="hover:bg-surface-2">
                <td className="font-mono text-xs">
                  <Link to="/fontes/$id" params={{ id: f.id }} className="font-semibold text-primary">
                    {f.id}
                  </Link>
                </td>
                <td className="max-w-lg">
                  <div className="font-semibold text-ink">{f.sigla}</div>
                  <div className="truncate text-xs text-muted">{f.titulo}</div>
                </td>
                <td className="text-xs text-muted">{f.tipo ?? "—"}</td>
                <td className="whitespace-nowrap">
                  <span className="font-mono text-xs">{dataBR(f.acesso)}</span>{" "}
                  {antiga(f.acesso) ? <Selo tom="alerta">+12 meses</Selo> : null}
                </td>
                <td className="text-right font-mono">{f.fatos}</td>
                <td className="text-right font-mono">{f.usos ? f.usos : <span className="text-muted">0</span>}</td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
      <Dialogo aberto={nova} aoFechar={() => setNova(false)} titulo="Nova fonte">
        <FormFonte
          novo
          inicial={{ acesso: new Date().toISOString().slice(0, 10) }}
          aoSalvar={(f) => criar.mutate(f)}
          pendente={criar.isPending}
          erro={criar.error}
        />
      </Dialogo>
    </>
  );
}

export function PaginaFonte() {
  const { id } = useParams({ from: "/area/fontes/$id" });
  const eu = useEu();
  const { data, error } = useQuery({ queryKey: ["fonte", id], queryFn: () => ler(api.fontes[":id"].$get({ param: { id } })) });
  const salvar = useMutation({
    mutationFn: ({ id: _id, ...f }: FonteInput) => ler(api.fontes[":id"].$put({ param: { id }, json: f })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fonte", id] });
      queryClient.invalidateQueries({ queryKey: ["fontes"] });
      queryClient.invalidateQueries({ queryKey: ["pendencias"] });
    },
  });
  if (error) return <Aviso>{msg(error)}</Aviso>;
  if (!data) return <Carregando />;
  const { fonte, fatos, usos } = data;
  const editor = eu?.papel === "admin" || eu?.papel === "editor";
  const fichasUsando = [...new Map(usos.map((u) => [u.fichaId, u])).values()];

  return (
    <>
      <Cabecalho
        titulo={fonte.sigla}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            <Link to="/fontes" className="hover:text-primary">
              Fontes
            </Link>
            <span className="font-mono text-xs">{fonte.id}</span>
            {antiga(fonte.acesso) ? (
              <Selo tom="alerta">conferida há mais de 12 meses</Selo>
            ) : (
              <Selo tom="ok">conferida em {dataBR(fonte.acesso)}</Selo>
            )}
            {fonte.url ? (
              <a href={fonte.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                abrir documento ↗
              </a>
            ) : null}
          </span>
        }
      />
      {salvar.data ? <Aviso tom="ok">Fonte salva. {salvar.data.revalidadas} ficha(s) revalidada(s).</Aviso> : null}
      <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
        <Cartao titulo="Dados da fonte">
          <fieldset disabled={!editor}>
            <FormFonte inicial={fonte} aoSalvar={(f) => salvar.mutate(f)} pendente={salvar.isPending} erro={salvar.error} />
          </fieldset>
        </Cartao>
        <div className="flex flex-col gap-4">
          <Cartao
            titulo={`Fatos conferidos (${fatos.length})`}
            acoes={
              editor ? (
                <Link to="/fatos/$id" params={{ id: "novo" }} search={{ fonte: fonte.id }} className="text-sm font-semibold text-primary">
                  Novo fato
                </Link>
              ) : null
            }
          >
            {fatos.length ? (
              <ul className="flex max-h-[28rem] flex-col gap-2 overflow-auto text-sm">
                {fatos.map((f) => (
                  <li key={f.id}>
                    <Link to="/fatos/$id" params={{ id: f.id }} className="font-mono text-xs font-semibold text-primary">
                      {f.id}
                    </Link>{" "}
                    {f.status !== "verificado" ? <Selo tom={f.status === "conflito" ? "perigo" : "alerta"}>{f.status}</Selo> : null}
                    <p className="text-xs">{f.afirmacao}</p>
                    {f.valor ? (
                      <p className="font-mono text-xs text-muted">
                        {f.valor} {f.unidade ?? ""} {f.localizador ? `· ${f.localizador}` : ""}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <Vazio>{fonte.semFato ? "Fonte citada inteira, sem fatos." : "Nenhum fato ainda."}</Vazio>
            )}
          </Cartao>
          <Cartao titulo={`Usada em ${fichasUsando.length} ficha(s)`}>
            <ul className="flex max-h-80 flex-col gap-1 overflow-auto text-sm">
              {fichasUsando.map((u) => (
                <li key={u.fichaId} className="flex items-center justify-between gap-2">
                  <Link to="/fichas/$id" params={{ id: u.fichaId }} className="truncate hover:text-primary">
                    {u.titulo}
                  </Link>
                  <span className="font-mono text-xs text-muted">{usos.filter((x) => x.fichaId === u.fichaId).length}×</span>
                </li>
              ))}
            </ul>
          </Cartao>
        </div>
      </div>
    </>
  );
}
